'use client'

import ConfirmModal from '@/components/confirm-modal'
import { FormField } from '@/components/form-section'
import { getTriggerEventLabels } from '@/lib/api/sales/automations-api'
import {
    createSalesFlow,
    deleteSalesFlow,
    getSalesFlowRuns,
    getSalesFlows,
    setSalesFlowActive,
    type SalesFlow,
    type SalesFlowRun,
} from '@/lib/api/sales/flows-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import type { SalesFlowRunStatus } from '@mediardex/contracts'
import { format, formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import { MdAccountTree, MdAdd, MdDelete, MdEdit, MdHistory, MdRefresh } from 'react-icons/md'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import RunDetailModal from './components/run-detail-modal'
import { FLOW_NODE_META, FLOW_RUN_STATUS, FLOW_TRIGGER_LABELS, formatMinutes } from './flow-meta'
import { FLOW_TEMPLATES } from './flow-templates'

type Tab = 'flows' | 'runs'

/**
 * Automações e fluxos (docs/sales-flows.md). Substitui as telas de automação e de
 * follow-up; o que já existia nelas chega aqui pelo backfill `sales-flows`.
 */
export default function FlowsPage() {
    const navigate = useNavigate()
    const [params, setParams] = useSearchParams()
    const eventLabels = getTriggerEventLabels(useServiceLabel()) as Record<string, string>
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('sales', 'automations')

    const tab: Tab = params.get('tab') === 'runs' ? 'runs' : 'flows'
    const setTab = (next: Tab) => setParams(prev => {
        const copy = new URLSearchParams(prev)
        copy.set('tab', next)
        return copy
    })

    const [flows, setFlows] = useState<SalesFlow[]>([])
    const [loadingFlows, setLoadingFlows] = useState(true)
    const [togglingId, setTogglingId] = useState<number | null>(null)
    const [flowToDelete, setFlowToDelete] = useState<SalesFlow | null>(null)
    const [deleting, setDeleting] = useState(false)

    const [showCreate, setShowCreate] = useState(false)
    const [newName, setNewName] = useState('')
    const [templateId, setTemplateId] = useState('stage-follow-up')
    const [creating, setCreating] = useState(false)

    const [runs, setRuns] = useState<SalesFlowRun[]>([])
    const [loadingRuns, setLoadingRuns] = useState(false)
    const runFlowId = params.get('flowId') ? Number(params.get('flowId')) : undefined
    const runStatus = (params.get('status') || undefined) as SalesFlowRunStatus | undefined
    const includeSimulations = params.get('simulations') === '1'
    const [openRunId, setOpenRunId] = useState<number | null>(null)

    const setRunFilter = (key: string, value?: string) =>
        setParams(prev => {
            const copy = new URLSearchParams(prev)
            if (value) copy.set(key, value)
            else copy.delete(key)
            return copy
        })

    const loadFlows = async () => {
        setLoadingFlows(true)
        const res = await getSalesFlows()
        setFlows(res.success && Array.isArray(res.result) ? res.result : [])
        setLoadingFlows(false)
    }

    const loadRuns = async () => {
        setLoadingRuns(true)
        const res = await getSalesFlowRuns({ flowId: runFlowId, status: runStatus, dryRun: includeSimulations ? undefined : false })
        setRuns(res.success && Array.isArray(res.result) ? res.result : [])
        setLoadingRuns(false)
    }

    useEffect(() => {
        loadFlows()
    }, [])

    useEffect(() => {
        if (tab === 'runs') loadRuns()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, runFlowId, runStatus, includeSimulations])

    const triggerBadges = (flow: SalesFlow) =>
        flow.triggers.map((t, i) => {
            const label = t.type === 'EVENT' && t.event ? eventLabels[t.event] ?? t.event : FLOW_TRIGGER_LABELS[t.type]
            return (
                <span key={i} className="badge badge-outline badge-sm">
                    {label}
                    {t.delayMinutes ? ` · ${formatMinutes(t.delayMinutes)}` : ''}
                </span>
            )
        })

    const toggle = async (flow: SalesFlow) => {
        setTogglingId(flow.id)
        const res = await setSalesFlowActive(flow.id, !flow.active)
        setTogglingId(null)
        if (!res.success) {
            toast.error(res.error || 'Não foi possível alterar o status')
            return
        }
        setFlows(prev => prev.map(f => (f.id === flow.id ? { ...f, active: res.result.active } : f)))
    }

    const create = async () => {
        const template = FLOW_TEMPLATES.find(t => t.id === templateId) ?? FLOW_TEMPLATES[0]
        const name = newName.trim() || template.name
        setCreating(true)
        try {
            const created = await createSalesFlow({ name, description: template.id === 'blank' ? undefined : template.description, ...template.settings })
            if (!created.success) {
                toast.error(created.error || 'Erro ao criar o fluxo')
                return
            }
            // O modelo abre no editor como alteração não publicada: tem etapas e setores em
            // branco, que a organização escolhe antes de publicar.
            navigate(`/sales/automations/flows/${created.result.id}`, { state: { template: template.id } })
        } finally {
            setCreating(false)
        }
    }

    const confirmDelete = async () => {
        if (!flowToDelete) return
        setDeleting(true)
        const res = await deleteSalesFlow(flowToDelete.id)
        setDeleting(false)
        if (!res.success) {
            toast.error(res.error || 'Erro ao excluir')
            return
        }
        toast.success('Fluxo excluído')
        setFlowToDelete(null)
        loadFlows()
    }

    const flowNames = useMemo(() => new Map(flows.map(f => [f.id, f.name])), [flows])

    return (
        <div className="space-y-6 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Automações e fluxos</h1>
                    <p className="mt-1 text-sm text-base-content/60">Gatilhos, mensagens, esperas e ações de CRM num só desenho.</p>
                </div>
                <div className="flex gap-2">
                    {canCreate && (
                        <button type="button" className="btn btn-primary btn-sm gap-1" onClick={() => setShowCreate(true)}>
                            <MdAdd /> Novo fluxo
                        </button>
                    )}
                </div>
            </div>

            <div role="tablist" className="tabs tabs-boxed w-fit">
                <button role="tab" type="button" className={`tab gap-1 ${tab === 'flows' ? 'tab-active' : ''}`} onClick={() => setTab('flows')}>
                    <MdAccountTree /> Fluxos
                </button>
                <button role="tab" type="button" className={`tab gap-1 ${tab === 'runs' ? 'tab-active' : ''}`} onClick={() => setTab('runs')}>
                    <MdHistory /> Execuções
                </button>
            </div>

            {tab === 'flows' &&
                (loadingFlows ? (
                    <div className="flex justify-center py-16">
                        <span className="loading loading-spinner loading-lg" />
                    </div>
                ) : flows.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-base-300 bg-base-100 p-10 text-center">
                        <MdAccountTree className="mx-auto mb-3 text-4xl text-base-content/30" />
                        <p className="font-medium">Nenhum fluxo ainda</p>
                        <p className="mt-1 text-sm text-base-content/60">Comece por um modelo, como o follow-up por etapa.</p>
                    </div>
                ) : (
                    <div className="grid gap-4 lg:grid-cols-2">
                        {flows.map(flow => (
                            <div key={flow.id} className="flex flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-5">
                                <div className="flex items-start gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="truncate font-semibold">{flow.name}</h2>
                                            {flow.legacyRef && <span className="badge badge-ghost badge-sm">Migrado</span>}
                                            {flow.draftSavedAt && <span className="badge badge-warning badge-sm">Rascunho não publicado</span>}
                                            {flow.launchable && <span className="badge badge-ghost badge-sm">No "/" do chat</span>}
                                        </div>
                                        {flow.description && <p className="mt-1 line-clamp-2 text-sm text-base-content/60">{flow.description}</p>}
                                    </div>
                                    {canUpdate && (
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-success toggle-sm mt-1"
                                            checked={flow.active}
                                            disabled={togglingId === flow.id}
                                            onChange={() => toggle(flow)}
                                            aria-label={flow.active ? 'Desativar' : 'Ativar'}
                                        />
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-1.5">{triggerBadges(flow)}</div>
                                <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-base-200 pt-3 text-xs text-base-content/50">
                                    <span>
                                        {flow._count?.nodes ?? 0} passos · {flow._count?.runs ?? 0} em andamento · atualizado{' '}
                                        {formatDistanceToNow(new Date(flow.updatedAt), { addSuffix: true, locale: ptBR })}
                                    </span>
                                    <div className="flex gap-1">
                                        <button type="button" className="btn btn-ghost btn-xs gap-1" onClick={() => { setRunFilter('flowId', String(flow.id)); setTab('runs') }}>
                                            <MdHistory /> Execuções
                                        </button>
                                        <button type="button" className="btn btn-ghost btn-xs gap-1" onClick={() => navigate(`/sales/automations/flows/${flow.id}`)}>
                                            <MdEdit /> Editar
                                        </button>
                                        {canDelete && (
                                            <button type="button" className="btn btn-ghost btn-xs gap-1 text-error" onClick={() => setFlowToDelete(flow)}>
                                                <MdDelete />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}

            {tab === 'runs' && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-base-300 bg-base-100 p-4">
                        <FormField label="Fluxo" htmlFor="runs-flow" className="min-w-56">
                            <select id="runs-flow" className="select select-bordered select-sm w-full" value={runFlowId ?? ''} onChange={e => setRunFilter('flowId', e.target.value)}>
                                <option value="">Todos</option>
                                {flows.map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.name}
                                    </option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label="Status" htmlFor="runs-status" className="min-w-44">
                            <select id="runs-status" className="select select-bordered select-sm w-full" value={runStatus ?? ''} onChange={e => setRunFilter('status', e.target.value)}>
                                <option value="">Todos</option>
                                {Object.entries(FLOW_RUN_STATUS).map(([value, { label }]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </FormField>
                        <label className="flex cursor-pointer items-center gap-2 pb-1.5">
                            <input type="checkbox" className="checkbox checkbox-sm" checked={includeSimulations} onChange={e => setRunFilter('simulations', e.target.checked ? '1' : undefined)} />
                            <span className="text-sm">Incluir simulações</span>
                        </label>
                        <button type="button" className="btn btn-ghost btn-sm ml-auto gap-1" onClick={loadRuns}>
                            <MdRefresh /> Atualizar
                        </button>
                    </div>

                    <div className="overflow-x-auto rounded-2xl border border-base-300 bg-base-100">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Fluxo</th>
                                    <th>Lead</th>
                                    <th>Status</th>
                                    <th>Passo atual</th>
                                    <th>Início</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingRuns ? (
                                    <tr>
                                        <td colSpan={6} className="py-10 text-center">
                                            <span className="loading loading-spinner" />
                                        </td>
                                    </tr>
                                ) : runs.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="py-10 text-center text-sm text-base-content/50">
                                            Nenhuma execução encontrada.
                                        </td>
                                    </tr>
                                ) : (
                                    runs.map(run => {
                                        const status = FLOW_RUN_STATUS[run.status]
                                        return (
                                            <tr key={run.id} className="cursor-pointer hover" onClick={() => setOpenRunId(run.id)}>
                                                <td className="text-base-content/50">{run.id}</td>
                                                <td>{run.flow?.name ?? flowNames.get(run.flowId) ?? `#${run.flowId}`}</td>
                                                <td>
                                                    <span className="block">{run.lead?.name ?? `Lead #${run.leadId}`}</span>
                                                    {run.lead?.phoneNumber && <span className="text-xs text-base-content/50">{run.lead.phoneNumber}</span>}
                                                </td>
                                                <td>
                                                    <div className="flex flex-wrap gap-1">
                                                        <span className={`badge badge-sm ${status.badge}`}>{status.label}</span>
                                                        {run.dryRun && <span className="badge badge-info badge-outline badge-sm">Simulação</span>}
                                                    </div>
                                                </td>
                                                <td className="text-sm text-base-content/70">
                                                    {run.status === 'WAITING' || run.status === 'RUNNING' ? run.currentNodeKey : (run.lastError ?? '—')}
                                                </td>
                                                <td className="whitespace-nowrap text-sm">{format(new Date(run.startedAt), 'dd/MM/yyyy HH:mm')}</td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showCreate && (
                <dialog className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="text-lg font-semibold">Novo fluxo</h3>
                        <p className="mt-1 text-sm text-base-content/60">O fluxo nasce desativado. Monte, simule e ative quando estiver pronto.</p>

                        <div className="mt-6 space-y-5">
                            <FormField label="Nome" htmlFor="new-flow-name" optional hint="Vazio usa o nome do modelo.">
                                <input id="new-flow-name" className="input input-bordered w-full" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
                            </FormField>
                            <FormField label="Começar por">
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {FLOW_TEMPLATES.map(template => (
                                        <button
                                            key={template.id}
                                            type="button"
                                            className={`flex flex-col items-start justify-start rounded-xl border p-4 text-left transition-colors ${
                                                templateId === template.id ? 'border-primary bg-primary/5' : 'border-base-300 hover:bg-base-200/60'
                                            }`}
                                            onClick={() => setTemplateId(template.id)}
                                        >
                                            <span className="block text-sm font-semibold">{template.name}</span>
                                            <span className="mt-1 block text-xs leading-relaxed text-base-content/60">{template.description}</span>
                                            {template.nodes.length > 1 && (
                                                <span className="mt-2 block text-[11px] text-base-content/40">
                                                    {template.nodes.filter(n => n.type !== 'START').map(n => FLOW_NODE_META[n.type].label).slice(0, 4).join(' › ')}
                                                    {template.nodes.length > 5 ? ' ›…' : ''}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </FormField>
                        </div>

                        <div className="modal-action">
                            <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-primary" disabled={creating} onClick={create}>
                                {creating && <span className="loading loading-spinner loading-xs" />}
                                Criar e abrir
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => setShowCreate(false)} />
                </dialog>
            )}

            {openRunId && <RunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} onChanged={loadRuns} />}

            <ConfirmModal
                isOpen={!!flowToDelete}
                onClose={() => setFlowToDelete(null)}
                onConfirm={confirmDelete}
                loading={deleting}
                title="Excluir fluxo"
                message={`O fluxo "${flowToDelete?.name ?? ''}" será excluído e as execuções em andamento serão canceladas.`}
                confirmButtonText="Excluir"
            />
        </div>
    )
}
